import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../utils/translations';

const { FiUser, FiCheck, FiActivity, FiSmile, FiGlobe, FiX } = FiIcons;

const UserProfileModal = ({ onSubmit, existingProfile, onClose }) => {
  const { t, language, changeLanguage } = useLanguage();
  
  const [formData, setFormData] = useState({
    firstName: existingProfile?.firstName || '',
    sex: existingProfile?.sex || '',
    age: existingProfile?.age || '',
    height_cm: existingProfile?.height_cm || '',
    weight_kg: existingProfile?.weight_kg || '',
    fitnessLevel: existingProfile?.fitnessLevel || 'incepator'
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = t('err_name');
    if (!formData.sex) newErrors.sex = t('err_sex');
    if (!formData.age || formData.age < 18 || formData.age > 100) {
      newErrors.age = t('err_age');
    }
    if (!formData.height_cm || formData.height_cm < 140 || formData.height_cm > 220) {
      newErrors.height_cm = t('err_height');
    }
    if (!formData.weight_kg || formData.weight_kg < 40 || formData.weight_kg > 200) {
      newErrors.weight_kg = t('err_weight');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        firstName: formData.firstName.trim(),
        age: parseInt(formData.age),
        height_cm: parseInt(formData.height_cm),
        weight_kg: parseFloat(formData.weight_kg)
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const inputClass = (error) => `
    w-full bg-slate-50 border 
    ${error ? 'border-red-400 focus:ring-red-200' : 'border-slate-200 focus:ring-blue-200'}
    rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-4 focus:border-blue-500 transition-all
  `;

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden my-8"
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white relative">
          {onClose && (
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl text-white" />
            </button>
          )}
          
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-md">
                <SafeIcon icon={FiActivity} className="text-2xl" />
              </div>
              <h2 className="text-2xl font-bold">{t('lets_start')}</h2>
            </div>
          </div>
          <p className="text-blue-100">{t('setup_profile')}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          
          {/* Language Selector */}
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <label className="block text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <SafeIcon icon={FiGlobe} /> {t('language')}
            </label>
            <select 
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* First Name Field */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">{t('firstname')}</label>
            <div className="relative">
              <input 
                type="text" 
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={inputClass(errors.firstName)}
                placeholder={t('firstname_placeholder')}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <SafeIcon icon={FiSmile} />
              </div>
            </div>
            {errors.firstName && <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.firstName}</p>}
          </div>

          {/* Gender Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">{t('gender')}</label>
            <div className="grid grid-cols-2 gap-4">
              {['femeie', 'barbat'].map(sex => (
                <button
                  key={sex}
                  type="button"
                  onClick={() => handleChange('sex', sex)}
                  className={`
                    relative py-3 px-4 rounded-xl border-2 font-medium transition-all
                    ${formData.sex === sex 
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' 
                      : 'border-slate-100 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}
                  `}
                >
                  {formData.sex === sex && (
                    <div className="absolute top-2 right-2 text-blue-600">
                      <SafeIcon icon={FiCheck} />
                    </div>
                  )}
                  {sex === 'femeie' ? t('female') : t('male')}
                </button>
              ))}
            </div>
            {errors.sex && <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.sex}</p>}
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">{t('age')}</label>
              <input 
                type="number" 
                value={formData.age}
                onChange={(e) => handleChange('age', e.target.value)}
                className={inputClass(errors.age)}
                placeholder="ex. 35"
              />
              {errors.age && <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.age}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">{t('height')}</label>
              <input 
                type="number" 
                value={formData.height_cm}
                onChange={(e) => handleChange('height_cm', e.target.value)}
                className={inputClass(errors.height_cm)}
                placeholder="ex. 170"
              />
              {errors.height_cm && <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.height_cm}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">{t('weight')}</label>
            <input 
              type="number" 
              step="0.1"
              value={formData.weight_kg}
              onChange={(e) => handleChange('weight_kg', e.target.value)}
              className={inputClass(errors.weight_kg)}
              placeholder="ex. 70"
            />
            {errors.weight_kg && <p className="text-red-500 text-xs mt-1.5 ml-1">{errors.weight_kg}</p>}
          </div>

          {/* Fitness Level */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">{t('activity_level')}</label>
            <div className="relative">
              <select 
                value={formData.fitnessLevel}
                onChange={(e) => handleChange('fitnessLevel', e.target.value)}
                className={`${inputClass(null)} appearance-none cursor-pointer`}
              >
                <option value="sedentar">{t('level_sedentary')}</option>
                <option value="incepator">{t('level_beginner')}</option>
                <option value="intermediar">{t('level_intermediate')}</option>
                <option value="avansat">{t('level_advanced')}</option>
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            type="submit"
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 hover:bg-blue-700 transition-all"
          >
            <SafeIcon icon={FiCheck} className="text-xl" />
            {t('generate_program')}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

export default UserProfileModal;